import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Tabs,
  Table,
  List,
  Tag,
  Button,
  message,
  Spin,
  Typography,
  Select,
  Empty,
  Row,
  Col,
  Statistic,
  Badge,
  Space,
  Divider,
  Avatar,
  Dropdown,
} from 'antd';
import {
  CheckCircleOutlined,
  DollarOutlined,
  HeartOutlined,
  UserOutlined,
  MessageOutlined,
  ClockCircleOutlined,
  CheckCircleFilled,
  ExclamationCircleOutlined,
  BellOutlined,
  ArrowLeftOutlined,
  LogoutOutlined,
  HomeOutlined,
  SettingOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import crecheApi from '../../services/crecheApi';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

export default function ResponsavelDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [criancas, setCriancas] = useState([]);
  const [comunicados, setComunicados] = useState([]);
  const [pagamentos, setPagamentos] = useState([]);
  const [fichaSaude, setFichaSaude] = useState({});
  const [presencas, setPresencas] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [filhoSelecionado, setFilhoSelecionado] = useState(null);
  const [nomeUsuario, setNomeUsuario] = useState('');

  const carregarDadosIniciais = async () => {
    try {
      setLoading(true);
      
      const usuario = JSON.parse(localStorage.getItem('usuario') || '{}');
      setNomeUsuario(usuario.nome || 'Responsável');

      const [criancasRes, comunicadosRes] = await Promise.all([
        crecheApi.listarMinhasCriancas(),
        crecheApi.listarComunicados(),
      ]);

      const criancasData = criancasRes || [];
      setCriancas(criancasData);
      setComunicados(comunicadosRes || []);

      if (criancasData.length > 0) {
        setFilhoSelecionado(criancasData[0].id);
        carregarDetalhes(criancasData[0].id);
      }
    } catch (err) {
      console.error(err);
      message.error('Falha ao carregar dados iniciais.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDadosIniciais();
  }, []);

  const carregarDetalhes = async (id) => {
    if (!id) return;
    try {
      setLoading(true);
      
      const resPagamentos = await crecheApi.listarMinhasPrestacoes(id);
      console.log('💰 Pagamentos recebidos:', resPagamentos);
      setPagamentos(resPagamentos || []);

      const resSaude = await crecheApi.obterMinhaFichaSaude(id).catch(() => ({}));
      setFichaSaude(resSaude || {});

      const resPresencas = await crecheApi.listarMinhasPresencas(id).catch(() => []);
      setPresencas(resPresencas || []);

      if (crecheApi.listarMeusCheckins) {
        const resCheckins = await crecheApi.listarMeusCheckins(id).catch(() => []);
        console.log('⏰ Check-ins recebidos:', resCheckins);
        setCheckins(resCheckins || []);
      }

      if (crecheApi.listarEventos) {
        const resEventos = await crecheApi.listarEventos().catch(() => []);
        console.log('📅 Eventos recebidos:', resEventos);
        setEventos(resEventos || []);
      }
      
    } catch (err) {
      console.error('❌ Erro ao carregar detalhes:', err);
    } finally {
      setLoading(false);
    }
  };

  const marcarComoLido = async (id) => {
    try {
      await crecheApi.marcarComunicadoComoLido(id);
      message.success('Comunicado marcado como lido.');
      setComunicados((prev) =>
        prev.map((c) => (c.id === id ? { ...c, lido: true } : c))
      );
    } catch {
      message.warning('Não foi possível marcar como lido.');
    }
  };

  const handleVoltar = () => {
    navigate(-1);
  };

  const handleInicio = () => {
    navigate('/');
  };

  const handleSair = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('usuario');
    message.success('Sessão encerrada com sucesso');
    navigate('/login');
  };

  const menuUsuario = [
    {
      key: 'perfil',
      icon: <UserOutlined />,
      label: 'Meu Perfil',
    },
    {
      key: 'configuracoes',
      icon: <SettingOutlined />,
      label: 'Configurações',
    },
    {
      type: 'divider',
    },
    {
      key: 'sair',
      icon: <LogoutOutlined />,
      label: 'Sair',
      danger: true,
      onClick: handleSair,
    },
  ];

  const stats = {
    totalCriancas: criancas.length,
    comunicadosNaoLidos: comunicados.filter((c) => !c.lido).length,
    pagamentosAtrasados: pagamentos.filter((p) => p.status === 'ATRASADO').length,
    presencasMes: presencas.filter(
      (p) => new Date(p.data).getMonth() === new Date().getMonth()
    ).length,
  };

  const colunasFilhos = [
    {
      title: 'Nome Completo',
      dataIndex: 'nomeCompleto',
      key: 'nome',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Classe',
      dataIndex: 'classe',
      key: 'classe',
      render: (text) => <Tag color="blue">{text || 'Não definida'}</Tag>,
    },
    {
      title: 'Grupo',
      dataIndex: 'grupo',
      key: 'grupo',
      render: (text) => <Tag color="cyan">{text || 'Não definido'}</Tag>,
    },
    {
      title: 'Estado da Matrícula',
      dataIndex: 'status',
      key: 'status',
      render: (val) => {
        const config = {
          ATIVA: { color: 'success', icon: <CheckCircleFilled /> },
          AGUARDANDO_PAGAMENTO: { color: 'warning', icon: <ClockCircleOutlined /> },
          INATIVA: { color: 'default', icon: <ExclamationCircleOutlined /> },
        };
        const { color, icon } = config[val] || config.INATIVA;
        return (
          <Tag color={color} icon={icon}>
            {val?.replace('_', ' ')}
          </Tag>
        );
      },
    },
  ];

  const colunasPagamentos = [
    {
      title: 'Período',
      dataIndex: 'mes',
      key: 'mes',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Valor',
      dataIndex: 'valor',
      render: (v) => (
        <Text style={{ fontSize: '15px', fontWeight: 500 }}>
          {v?.toLocaleString()} MT
        </Text>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      render: (s) => {
        const config = {
          PAGO: { color: 'success', icon: <CheckCircleFilled /> },
          ATRASADO: { color: 'error', icon: <ExclamationCircleOutlined /> },
          PENDENTE: { color: 'warning', icon: <ClockCircleOutlined /> },
        };
        const { color, icon } = config[s] || config.PENDENTE;
        return (
          <Tag color={color} icon={icon}>
            {s}
          </Tag>
        );
      },
    },
    {
      title: 'Vencimento',
      dataIndex: 'dataVencimento',
      render: (v) => (v ? new Date(v).toLocaleDateString('pt-PT') : '-'),
    },
    {
      title: 'Observações',
      dataIndex: 'observacoes',
      key: 'observacoes',
      ellipsis: true,
      render: (text) => <Text type="secondary">{text || '-'}</Text>,
    },
  ];

  const colunasPresencas = [
    {
      title: 'Data',
      dataIndex: 'data',
      key: 'data',
      render: (v) => {
        const data = new Date(v);
        return <Text strong>{data.toLocaleDateString('pt-PT')}</Text>;
      },
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => {
        const config = {
          PRESENTE: { color: 'success', icon: <CheckCircleFilled /> },
          FALTOU: { color: 'error', icon: <ExclamationCircleOutlined /> },
          JUSTIFICADO: { color: 'warning', icon: <ClockCircleOutlined /> },
        };
        const { color, icon } = config[estado] || { color: 'default', icon: null };
        return (
          <Tag color={color} icon={icon}>
            {estado || 'Não registado'}
          </Tag>
        );
      },
    },
    {
      title: 'Período / Observação',
      dataIndex: 'observacao',
      key: 'observacao',
      render: (text) => <Text type="secondary">{text || '-'}</Text>,
    },
  ];

  const colunasCheckins = [
    {
      title: 'Data',
      dataIndex: 'data',
      key: 'data',
      render: (v) => {
        const data = new Date(v);
        const diaSemana = data.toLocaleDateString('pt-PT', { weekday: 'short' });
        return (
          <div>
            <Text strong>{data.toLocaleDateString('pt-PT')}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {diaSemana}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Entrada',
      key: 'entrada',
      render: (_, record) => (
        <div>
          <Text strong style={{ color: '#52c41a' }}>
            {record.horaEntrada || '-'}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.responsavelEntrada || '-'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Saída',
      key: 'saida',
      render: (_, record) => (
        <div>
          <Text strong style={{ color: record.horaSaida ? '#ff4d4f' : '#999' }}>
            {record.horaSaida || 'Em andamento'}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.responsavelSaida || '-'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        if (record.horaSaida) {
          return <Tag color="success" icon={<CheckCircleFilled />}>Completo</Tag>;
        }
        return <Tag color="processing" icon={<ClockCircleOutlined />}>Na creche</Tag>;
      },
    },
  ];

  const seletorFilho = criancas.length > 0 && (
    <div style={{ marginBottom: 24 }}>
      <Space align="center">
        <UserOutlined style={{ fontSize: 16, color: '#1890ff' }} />
        <Text strong style={{ fontSize: 15 }}>
          Selecionar Filho:
        </Text>
        <Select
          size="large"
          style={{ minWidth: 280 }}
          value={filhoSelecionado}
          onChange={(v) => {
            setFilhoSelecionado(v);
            carregarDetalhes(v);
          }}
        >
          {criancas.map((c) => (
            <Option key={c.id} value={c.id}>
              <Space>
                <UserOutlined />
                {c.nomeCompleto}
              </Space>
            </Option>
          ))}
        </Select>
      </Space>
    </div>
  );

  const tabs = [
    {
      key: 'filhos',
      label: (
        <span style={{ fontSize: 15 }}>
          <UserOutlined /> Meus Filhos
        </span>
      ),
      children: (
        <Card variant="borderless" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Table
            rowKey="id"
            dataSource={criancas}
            columns={colunasFilhos}
            pagination={false}
            loading={loading}
            locale={{ emptyText: <Empty description="Nenhuma criança vinculada" /> }}
          />
        </Card>
      ),
    },
    {
      key: 'comunicados',
      label: (
        <span style={{ fontSize: 15 }}>
          <Badge count={stats.comunicadosNaoLidos} offset={[10, 0]}>
            <MessageOutlined /> Comunicados
          </Badge>
        </span>
      ),
      children: (
        <Card variant="borderless" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <List
            itemLayout="vertical"
            dataSource={comunicados}
            locale={{ emptyText: <Empty description="Nenhum comunicado disponível" /> }}
            renderItem={(item) => (
              <List.Item
                key={item.id}
                actions={[
                  !item.lido && (
                    <Button
                      type="primary"
                      ghost
                      size="small"
                      onClick={() => marcarComoLido(item.id)}
                      icon={<CheckCircleOutlined />}
                    >
                      Marcar como lido
                    </Button>
                  ),
                ]}
                style={{
                  background: !item.lido ? '#f6ffed' : 'transparent',
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 8,
                }}
              >
                <List.Item.Meta
                  avatar={
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: !item.lido ? '#52c41a' : '#d9d9d9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <BellOutlined style={{ color: 'white', fontSize: 18 }} />
                    </div>
                  }
                  title={
                    <Space>
                      <Text strong style={{ fontSize: 16 }}>
                        {item.titulo}
                      </Text>
                      {!item.lido && (
                        <Tag color="success" icon={<BellOutlined />}>
                          Novo
                        </Tag>
                      )}
                    </Space>
                  }
                  description={
                    <Text type="secondary">
                      {new Date(item.createdAt).toLocaleString('pt-PT')}
                    </Text>
                  }
                />
                <Paragraph style={{ marginTop: 12, marginBottom: 0 }}>
                  {item.corpo}
                </Paragraph>
              </List.Item>
            )}
          />
        </Card>
      ),
    },
    {
      key: 'pagamentos',
      label: (
        <span style={{ fontSize: 15 }}>
          <Badge count={stats.pagamentosAtrasados} offset={[10, 0]}>
            <DollarOutlined /> Pagamentos
          </Badge>
        </span>
      ),
      children: (
        <Card variant="borderless" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          {seletorFilho}
          <Table
            rowKey="id"
            dataSource={pagamentos}
            columns={colunasPagamentos}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: <Empty description="Sem pagamentos registados" /> }}
          />
        </Card>
      ),
    },
    {
      key: 'saude',
      label: (
        <span style={{ fontSize: 15 }}>
          <HeartOutlined /> Ficha de Saúde
        </span>
      ),
      children: (
        <Card variant="borderless" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          {seletorFilho}
          <Divider orientation="left">Informações Médicas</Divider>
          <List
            grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 3, xl: 3 }}
            dataSource={[
              {
                label: 'Alergias',
                valor: fichaSaude.alergias
                  ? (Array.isArray(fichaSaude.alergias)
                      ? fichaSaude.alergias.join(', ')
                      : fichaSaude.alergias)
                  : 'Nenhuma',
              },
              {
                label: 'Vacinas',
                valor: fichaSaude.vacinas
                  ? (Array.isArray(fichaSaude.vacinas)
                      ? fichaSaude.vacinas.map(v => v.nome || v).join(', ')
                      : 'Ver detalhes')
                  : 'Não informado',
              },
              {
                label: 'Medicação Regular',
                valor: fichaSaude.medicacao
                  ? (Array.isArray(fichaSaude.medicacao)
                      ? fichaSaude.medicacao.map(m => m.nome || m).join(', ')
                      : 'Ver detalhes')
                  : 'Nenhuma',
              },
              {
                label: 'Contato Médico',
                valor: fichaSaude.contactoMedico || 'Não informado',
              },
              {
                label: 'Observações',
                valor: fichaSaude.observacoes || 'Nenhuma',
              },
            ]}
            locale={{
              emptyText: <Empty description="Nenhuma informação médica disponível" />,
            }}
            renderItem={(item) => (
              <List.Item>
                <Card
                  size="small"
                  hoverable
                  style={{ borderRadius: 8, background: '#fafafa' }}
                >
                  <Statistic
                    title={item.label}
                    value={item.valor}
                    valueStyle={{ fontSize: 14, color: '#262626', whiteSpace: 'pre-wrap' }}
                  />
                </Card>
              </List.Item>
            )}
          />
        </Card>
      ),
    },
    {
      key: 'checkins',
      label: (
        <span style={{ fontSize: 15 }}>
          <ClockCircleOutlined /> Check-ins
        </span>
      ),
      children: (
        <Card variant="borderless" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          {seletorFilho}
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">Últimos 7 dias de entradas e saídas</Text>
          </div>
          <Table
            rowKey="id"
            dataSource={checkins}
            columns={colunasCheckins}
            pagination={{ pageSize: 10 }}
            loading={loading}
            locale={{ emptyText: <Empty description="Nenhum check-in registado" /> }}
          />
        </Card>
      ),
    },
    {
      key: 'eventos',
      label: (
        <span style={{ fontSize: 15 }}>
          <CalendarOutlined /> Eventos
        </span>
      ),
      children: (
        <Card variant="borderless" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Divider orientation="left">Próximos Eventos</Divider>
          <List
            grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 4 }}
            dataSource={eventos}
            locale={{ emptyText: <Empty description="Nenhum evento programado" /> }}
            renderItem={(evento) => (
              <List.Item>
                <Card
                  hoverable
                  style={{
                    borderRadius: 12,
                    border: `2px solid ${evento.tipo === 'FESTA' ? '#f5576c' : '#667eea'}`,
                    background: `linear-gradient(135deg, ${
                      evento.tipo === 'FESTA' ? '#f093fb15' : '#667eea15'
                    } 0%, ${evento.tipo === 'FESTA' ? '#f5576c15' : '#764ba215'} 100%)`,
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>
                      {evento.tipo === 'FESTA' ? '🎉' : '📅'}
                    </div>
                    <Title level={5} style={{ marginBottom: 8 }}>
                      {evento.titulo}
                    </Title>
                    <Tag
                      color={evento.tipo === 'FESTA' ? 'magenta' : 'blue'}
                      style={{ fontSize: 13 }}
                    >
                      {new Date(evento.data).toLocaleDateString('pt-PT', {
                        day: 'numeric',
                        month: 'long',
                      })}
                    </Tag>
                    {evento.descricao && (
                      <Paragraph
                        type="secondary"
                        style={{ marginTop: 12, fontSize: 13 }}
                        ellipsis={{ rows: 2 }}
                      >
                        {evento.descricao}
                      </Paragraph>
                    )}
                  </div>
                </Card>
              </List.Item>
            )}
          />
        </Card>
      ),
    },
    {
      key: 'presencas',
      label: (
        <span style={{ fontSize: 15 }}>
          <CheckCircleOutlined /> Presenças
        </span>
      ),
      children: (
        <Card variant="borderless" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          {seletorFilho}
          <Table
            rowKey="id"
            dataSource={presencas}
            columns={colunasPresencas}
            pagination={{ pageSize: 10 }}
            loading={loading}
            locale={{ emptyText: <Empty description="Nenhuma presença registada" /> }}
          />
        </Card>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <div
        style={{
          marginBottom: 32,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 16,
          padding: '32px',
          boxShadow: '0 8px 24px rgba(102, 126, 234, 0.25)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleVoltar}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
              }}
            >
              Voltar
            </Button>
            <Button
              icon={<HomeOutlined />}
              onClick={handleInicio}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
              }}
            >
              Início
            </Button>
          </Space>

          <Dropdown menu={{ items: menuUsuario }} placement="bottomRight">
            <Button
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Avatar icon={<UserOutlined />} style={{ background: '#fff', color: '#667eea' }} />
              <span>{nomeUsuario}</span>
            </Button>
          </Dropdown>
        </div>

        <div>
          <Title level={2} style={{ marginBottom: 8, color: 'white', fontWeight: 700 }}>
            Painel do Responsável
          </Title>
          <Text style={{ fontSize: 16, color: 'rgba(255,255,255,0.95)', lineHeight: 1.6 }}>
            Olá, <strong>{nomeUsuario}</strong>! 👋
            <br />
            Acompanhe aqui todas as informações sobre seus filhos, comunicados da instituição e situação de pagamentos.
          </Text>
        </div>
      </div>

      {loading && !criancas.length ? (
        <div style={{ textAlign: 'center', paddingTop: 80 }}>
          <Spin size="large" tip="Carregando informações..." />
        </div>
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card
                variant="borderless"
                style={{
                  borderRadius: 12,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                <Statistic
                  title={<Text style={{ color: 'rgba(255,255,255,0.85)' }}>Meus Filhos</Text>}
                  value={stats.totalCriancas}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#fff', fontSize: 32 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card
                variant="borderless"
                style={{
                  borderRadius: 12,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                }}
              >
                <Statistic
                  title={
                    <Text style={{ color: 'rgba(255,255,255,0.85)' }}>
                      Comunicados Não Lidos
                    </Text>
                  }
                  value={stats.comunicadosNaoLidos}
                  prefix={<MessageOutlined />}
                  valueStyle={{ color: '#fff', fontSize: 32 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card
                variant="borderless"
                style={{
                  borderRadius: 12,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                }}
              >
                <Statistic
                  title={
                    <Text style={{ color: 'rgba(255,255,255,0.85)' }}>
                      Pagamentos Atrasados
                    </Text>
                  }
                  value={stats.pagamentosAtrasados}
                  prefix={<ExclamationCircleOutlined />}
                  valueStyle={{ color: '#fff', fontSize: 32 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card
                variant="borderless"
                style={{
                  borderRadius: 12,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
                }}
              >
                <Statistic
                  title={
                    <Text style={{ color: 'rgba(255,255,255,0.85)' }}>
                      Presenças este Mês
                    </Text>
                  }
                  value={stats.presencasMes}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#fff', fontSize: 32 }}
                />
              </Card>
            </Col>
          </Row>

          <Card
            variant="borderless"
            style={{
              borderRadius: 12,
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            }}
          >
            <Tabs
              defaultActiveKey="filhos"
              items={tabs}
              size="large"
              tabBarStyle={{ marginBottom: 24 }}
            />
          </Card>
        </>
      )}
    </div>
  );
}